/**
 * In-browser speech recognition: Whisper small model via Transformers.js (ONNX + WebAssembly).
 * SYSTRAN faster-whisper does not run in the browser; this is the standard client-side equivalent.
 */

let transcriberPromise = null;

export function mergeFloat32Chunks(chunks) {
  const total = chunks.reduce((sum, c) => sum + c.length, 0);
  const out = new Float32Array(total);
  let offset = 0;
  for (const c of chunks) {
    out.set(c, offset);
    offset += c.length;
  }
  return out;
}

export function resampleTo16kHz(float32, sourceSampleRate) {
  if (sourceSampleRate === 16000) {
    return float32;
  }
  const ratio = sourceSampleRate / 16000;
  const newLength = Math.round(float32.length / ratio);
  const out = new Float32Array(newLength);
  for (let i = 0; i < newLength; i++) {
    const src = i * ratio;
    const j = Math.floor(src);
    const f = src - j;
    const s0 = float32[j] ?? 0;
    const s1 = float32[j + 1] ?? s0;
    out[i] = s0 * (1 - f) + s1 * f;
  }
  return out;
}

export async function getWhisperTranscriber(progressCallback) {
  if (!transcriberPromise) {
    transcriberPromise = (async () => {
      const { pipeline, env } = await import('@xenova/transformers');
      env.allowLocalModels = false;
      return pipeline('automatic-speech-recognition', 'Xenova/whisper-tiny.en', {
        quantized: true,
        progress_callback: progressCallback,
      });
    })();
  }
  return transcriberPromise;
}

export async function transcribePcm(float32, sourceSampleRate, progressCallback) {
  const audio16k = resampleTo16kHz(float32, sourceSampleRate);
  if (audio16k.length < 3200) {
    throw new Error('Recording too short — hold the mic and speak a little longer.');
  }
  const transcriber = await getWhisperTranscriber(progressCallback);
  const result = await transcriber(audio16k, {
    chunk_length_s: 30,
    stride_length_s: 5,
    return_timestamps: false,
  });
  const text = typeof result?.text === 'string' ? result.text : '';
  return text.trim();
}
